import * as THREE from "three";
import {
  attachElectricalRoomFeatures,
  type ElectricalRoomKit,
} from "./electricalRoom";
import { OUTLET_BENCH, WORKSHOP_SIZE } from "./workshopLayout";

/**
 * Electrical garage / utility room — the room itself is the experiment.
 *
 * Layout (meters, spawn faces −Z):
 * - Footprint 8.5 × 7 × 3, origin-centered xz
 * - −Z wall: training control panel + workshop sign
 * - −X wall: LIGHT B switch, outlet-a, exposed Outlet Assembly repair bay
 * - +X / −Z corner: pedestal fan
 * - Ceiling: two shop fixtures + right-wall sconce
 *
 * Quest-conscious: shared mats, dim hemisphere, one 512 shadow directional,
 * no extra lights, no bloom, low-poly props.
 */

export interface WorkshopEnvironment extends ElectricalRoomKit {
  room: THREE.Group;
  /** Exposed wall-box surround for Outlet Assembly (left wall). */
  outletWallBox: THREE.Group;
}

export {
  attachElectricalRoomFeatures,
  ELECTRICAL_ROOM_STATION_ID,
  ELECTRICAL_ROOM_NODE_IDS,
  ELECTRICAL_ROOM_OUTPUT_IDS,
  type CircuitIndicator,
  type ElectricalRoomJumper,
  type ElectricalRoomKit,
  type ElectricalRoomNodeId,
  type ElectricalRoomOutputId,
} from "./electricalRoom";

/**
 * Preferred entry — builds the garage shell, training panel, fixtures, and
 * the left-wall repair bay. Returns mesh hooks for hands / station work.
 */
export function buildElectricalRoom(scene: THREE.Scene): WorkshopEnvironment {
  const room = new THREE.Group();
  room.name = "workshop-room";

  const { width, depth, height } = WORKSHOP_SIZE;

  const concrete = new THREE.MeshStandardMaterial({
    color: 0x3a3834,
    roughness: 0.96,
    metalness: 0.02,
  });
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(width, depth), concrete);
  floor.rotation.x = -Math.PI / 2;
  floor.receiveShadow = true;
  floor.name = "floor";
  room.add(floor);

  // Worn path from spawn toward the panel — garage floor, not a classroom aisle.
  const path = new THREE.Mesh(
    new THREE.PlaneGeometry(1.7, depth * 0.62),
    new THREE.MeshStandardMaterial({
      color: 0x45423c,
      roughness: 1.0,
      metalness: 0.0,
    }),
  );
  path.rotation.x = -Math.PI / 2;
  path.position.set(0, 0.001, -0.15);
  path.receiveShadow = true;
  path.name = "floor-path";
  room.add(path);

  const wallMat = new THREE.MeshStandardMaterial({
    color: 0x4a5048,
    roughness: 0.95,
    metalness: 0.02,
  });
  const dadoMat = new THREE.MeshStandardMaterial({
    color: 0x2f3430,
    roughness: 0.9,
    metalness: 0.04,
  });

  const backWall = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height),
    wallMat,
  );
  backWall.position.set(0, height / 2, -depth / 2);
  backWall.receiveShadow = true;
  backWall.name = "wall-back";
  room.add(backWall);

  const leftWall = new THREE.Mesh(
    new THREE.PlaneGeometry(depth, height),
    wallMat,
  );
  leftWall.rotation.y = Math.PI / 2;
  leftWall.position.set(-width / 2, height / 2, 0);
  leftWall.name = "wall-left";
  room.add(leftWall);

  const rightWall = new THREE.Mesh(
    new THREE.PlaneGeometry(depth, height),
    wallMat,
  );
  rightWall.rotation.y = -Math.PI / 2;
  rightWall.position.set(width / 2, height / 2, 0);
  rightWall.name = "wall-right";
  room.add(rightWall);

  const frontWall = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height),
    wallMat,
  );
  frontWall.rotation.y = Math.PI;
  frontWall.position.set(0, height / 2, depth / 2);
  frontWall.name = "wall-entrance";
  room.add(frontWall);

  addWainscot(room, dadoMat, width, depth);
  addCeiling(room, width, depth, height);
  addEntranceDoor(room, width, depth);
  addRightWallPegboard(room, width);
  addFramingPlate(room, depth);

  const outletWallBox = buildOutletWallBox();
  room.add(outletWallBox);

  const kit = attachElectricalRoomFeatures(room);

  scene.add(room);
  addLighting(scene);

  return { room, outletWallBox, ...kit };
}

/** Alias kept so main / stations still compile against the workshop entry. */
export function buildWorkshopEnvironment(
  scene: THREE.Scene,
): WorkshopEnvironment {
  return buildElectricalRoom(scene);
}

export function buildRoom(scene: THREE.Scene): WorkshopEnvironment {
  return buildElectricalRoom(scene);
}

export const buildWorkshop = buildElectricalRoom;

function addWainscot(
  room: THREE.Group,
  mat: THREE.MeshStandardMaterial,
  width: number,
  depth: number,
): void {
  const h = 1.08;
  const y = h / 2;
  const t = 0.04;

  const back = new THREE.Mesh(new THREE.BoxGeometry(width, h, t), mat);
  back.position.set(0, y, -depth / 2 + t / 2);
  back.name = "dado-back";
  room.add(back);

  const left = new THREE.Mesh(new THREE.BoxGeometry(t, h, depth), mat);
  left.position.set(-width / 2 + t / 2, y, 0);
  left.name = "dado-left";
  room.add(left);

  const right = new THREE.Mesh(new THREE.BoxGeometry(t, h, depth), mat);
  right.position.set(width / 2 - t / 2, y, 0);
  right.name = "dado-right";
  room.add(right);
}

function addCeiling(
  room: THREE.Group,
  width: number,
  depth: number,
  height: number,
): void {
  const ceilMat = new THREE.MeshStandardMaterial({
    color: 0x24282c,
    roughness: 0.92,
    metalness: 0.04,
  });
  const ceiling = new THREE.Mesh(
    new THREE.PlaneGeometry(width, depth),
    ceilMat,
  );
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.y = height;
  ceiling.name = "ceiling";
  room.add(ceiling);

  const joistMat = new THREE.MeshStandardMaterial({
    color: 0x2c302c,
    roughness: 0.88,
    metalness: 0.05,
  });
  for (const z of [-1.8, 0.15, 2.05]) {
    const joist = new THREE.Mesh(
      new THREE.BoxGeometry(width - 0.3, 0.1, 0.12),
      joistMat,
    );
    joist.position.set(0, height - 0.08, z);
    joist.name = `joist-${z}`;
    room.add(joist);
  }
}

function addEntranceDoor(
  room: THREE.Group,
  _width: number,
  depth: number,
): void {
  const frameMat = new THREE.MeshStandardMaterial({
    color: 0x3a3e42,
    roughness: 0.55,
    metalness: 0.35,
  });
  const doorMat = new THREE.MeshStandardMaterial({
    color: 0x2a2e32,
    roughness: 0.7,
    metalness: 0.2,
  });

  const door = new THREE.Mesh(new THREE.BoxGeometry(1.15, 2.15, 0.06), doorMat);
  door.position.set(0, 1.08, depth / 2 - 0.03);
  door.name = "entrance-door";
  room.add(door);

  const frame = new THREE.Mesh(new THREE.BoxGeometry(1.28, 2.28, 0.08), frameMat);
  frame.position.set(0, 1.14, depth / 2 - 0.07);
  frame.name = "entrance-frame";
  room.add(frame);

  const kick = new THREE.Mesh(
    new THREE.BoxGeometry(1.12, 0.22, 0.02),
    frameMat,
  );
  kick.position.set(0, 0.14, depth / 2 - 0.01);
  kick.name = "entrance-kick";
  room.add(kick);
}

function addRightWallPegboard(room: THREE.Group, width: number): void {
  const board = new THREE.Mesh(
    new THREE.BoxGeometry(0.03, 1.15, 1.8),
    new THREE.MeshStandardMaterial({
      color: 0x6a4a32,
      roughness: 0.85,
      metalness: 0.05,
    }),
  );
  board.position.set(width / 2 - 0.05, 1.55, 1.15);
  board.name = "pegboard";
  room.add(board);

  const pegMat = new THREE.MeshStandardMaterial({
    color: 0x8a9098,
    roughness: 0.4,
    metalness: 0.6,
  });
  const pegGeo = new THREE.CylinderGeometry(0.008, 0.008, 0.05, 6);
  for (let i = 0; i < 6; i++) {
    const peg = new THREE.Mesh(pegGeo, pegMat);
    peg.rotation.z = Math.PI / 2;
    peg.position.set(width / 2 - 0.08, 1.85 - (i % 3) * 0.28, 0.55 + Math.floor(i / 3) * 0.7);
    peg.name = `peg-${i}`;
    room.add(peg);
  }

  const shelf = new THREE.Mesh(
    new THREE.BoxGeometry(0.28, 0.04, 1.1),
    new THREE.MeshStandardMaterial({
      color: 0x4a4036,
      roughness: 0.8,
      metalness: 0.08,
    }),
  );
  shelf.position.set(width / 2 - 0.2, 0.92, 1.15);
  shelf.castShadow = true;
  shelf.name = "side-shelf";
  room.add(shelf);
}

function addFramingPlate(room: THREE.Group, depth: number): void {
  const plate = makeCanvasLabel(
    "NON-ENERGIZED  ·  LOW-VOLTAGE TRAINING MOCKUP",
    {
      width: 1.85,
      height: 0.1,
      fontSize: 22,
      fill: "#a8b8a8",
      bg: "rgba(18,28,24,0.82)",
    },
  );
  plate.position.set(0, 1.95, -depth / 2 + 0.05);
  plate.name = "framing-plate";
  room.add(plate);
}

function buildOutletWallBox(): THREE.Group {
  const g = new THREE.Group();
  g.name = "outlet-wall-box";
  g.position.set(OUTLET_BENCH.x, 0, OUTLET_BENCH.z);
  g.rotation.y = OUTLET_BENCH.yaw;
  g.userData = { station: "outlet-assembly", kind: "wall-box" };

  const studMat = new THREE.MeshStandardMaterial({
    color: 0x6b5540,
    roughness: 0.78,
    metalness: 0.04,
  });
  const steelMat = new THREE.MeshStandardMaterial({
    color: 0x5a6270,
    roughness: 0.5,
    metalness: 0.45,
  });
  const shelfMat = new THREE.MeshStandardMaterial({
    color: 0x4e453a,
    roughness: 0.75,
    metalness: 0.06,
  });

  // Cut drywall bay — exposed repair, not a walk-up table.
  const bay = new THREE.Mesh(
    new THREE.BoxGeometry(1.25, 0.85, 0.04),
    new THREE.MeshStandardMaterial({
      color: 0x2a2e32,
      roughness: 0.9,
      metalness: 0.05,
    }),
  );
  bay.position.set(0.12, 1.15, -0.14);
  bay.name = "outlet-wall-box-bay";
  g.add(bay);

  for (const x of [-0.42, 0.66]) {
    const stud = new THREE.Mesh(new THREE.BoxGeometry(0.04, 1.35, 0.08), studMat);
    stud.position.set(x, 1.05, -0.1);
    stud.castShadow = true;
    stud.name = `outlet-stud-${x}`;
    g.add(stud);
  }

  const shelf = new THREE.Mesh(
    new THREE.BoxGeometry(1.22, 0.05, 0.38),
    shelfMat,
  );
  shelf.position.set(0.12, OUTLET_BENCH.y - 0.03, 0.02);
  shelf.castShadow = true;
  shelf.receiveShadow = true;
  shelf.name = "outlet-repair-shelf";
  g.add(shelf);

  const bracketGeo = new THREE.BoxGeometry(0.04, 0.18, 0.18);
  for (const x of [-0.38, 0.62]) {
    const bracket = new THREE.Mesh(bracketGeo, steelMat);
    bracket.position.set(x, OUTLET_BENCH.y - 0.14, -0.02);
    bracket.name = `outlet-bracket-${x}`;
    g.add(bracket);
  }

  const tag = makeCanvasLabel("WALL BOX  ·  REPAIR", {
    width: 0.55,
    height: 0.06,
    fontSize: 24,
    fill: "#d7dde8",
    bg: "rgba(20,24,32,0.78)",
  });
  tag.position.set(0.12, 1.58, -0.11);
  g.add(tag);

  return g;
}

function makeCanvasLabel(
  text: string,
  opts: {
    width: number;
    height: number;
    fontSize: number;
    fill: string;
    bg: string;
  },
): THREE.Mesh {
  const canvas = document.createElement("canvas");
  canvas.width = 768;
  canvas.height = 128;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = opts.bg;
  roundRect(ctx, 8, 12, canvas.width - 16, canvas.height - 24, 12);
  ctx.fill();
  ctx.fillStyle = opts.fill;
  ctx.font = `700 ${opts.fontSize}px system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, canvas.width / 2, canvas.height / 2 + 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;

  return new THREE.Mesh(
    new THREE.PlaneGeometry(opts.width, opts.height),
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
    }),
  );
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function addLighting(scene: THREE.Scene): void {
  // Dim hemisphere + one keyed directional (512). Fixture emissive is primary.
  const ambient = new THREE.HemisphereLight(0xb8c4b0, 0x2a2a26, 0.42);
  ambient.name = "hemi";
  scene.add(ambient);

  const key = new THREE.DirectionalLight(0xfff2d8, 0.55);
  key.name = "key-light";
  key.position.set(2.4, 4.0, 2.8);
  key.castShadow = true;
  key.shadow.mapSize.set(512, 512);
  key.shadow.camera.near = 0.5;
  key.shadow.camera.far = 18;
  key.shadow.camera.left = -5;
  key.shadow.camera.right = 5;
  key.shadow.camera.top = 4;
  key.shadow.camera.bottom = -4;
  key.target.position.set(0, 1.0, -1.2);
  scene.add(key);
  scene.add(key.target);
}
